import React from 'react';
import css from './NameNotes.module.scss';
import { OPEN_THIS_SCENE } from '../viewModel/refusals';
import { NAME_GAP_HEADLINE } from '../viewModel/publicNames';
import type { NameGap } from '../viewModel/publicNames';

/** T18-04: what the panel is called, said as the fact it is about. */
export const NAMES_NOT_SHOWN = "Names Basil can't show";

export type NameNotesProps = {
  /** One entry per name the script could not spell, in the order they appear in the script. */
  notes: NameGap[];
  /** Go to the line the note is about. The caret moves; nothing in the episode does. */
  onGoToLine: (line: number) => void;
  /** The protected exit into the scene's own editor: it asks before anything unapplied is lost. */
  onOpenScene?: (branchId: number) => void;
};

/**
 * T18-04 — the guidance for a name that cannot be shown, in a space of its own.
 *
 * The margin cannot hold it. Every row in that margin is positioned against the height of the
 * script line beside it, because that is what keeps a label on the line it is about; a paragraph in
 * one of those rows is taller than the row, so it hangs down across the rows beneath it and over
 * the next note. Two speakers whose names could not be shown, on neighbouring lines, overlapped each other.
 *
 * So the notes are a panel. It is in the ordinary flow of the surface, above the workspace: each
 * note is a block that takes the height its own words need and pushes the next one down. Overlap is
 * not styled away here, it is structurally impossible. The margin keeps a one-line marker beside
 * each affected label, so a writer can still see WHICH line each note is about, and each note names
 * its scene and line and carries the protected exit into that scene.
 *
 * Nothing here is a suggestion and nothing here collapses: the guidance says to leave a label
 * alone, and a writer who cannot see it will not leave it alone.
 */
export function NameNotes(props: NameNotesProps) {
  const { notes, onGoToLine, onOpenScene } = props;

  if (notes.length === 0) {
    return null;
  }

  return (
    <div className={css.panel} data-testid="basil-name-notes">
      <h2 className={css.heading}>{NAMES_NOT_SHOWN}</h2>
      <ul className={css.list}>
        {notes.map((note) => (
          <li className={css.note} key={`${note.domain}-${note.line}`} data-line={note.line}>
            <b className={css.noteHeadline}>{NAME_GAP_HEADLINE[note.domain]}</b>
            <span className={css.sentence}>{note.note}</span>
            <span className={css.where}>
              <button
                type="button"
                className={css.lineLink}
                onClick={() => onGoToLine(note.line)}
              >
                {`${note.sceneName || 'script'} · line ${note.line}`}
              </button>
              {note.branchId != null && onOpenScene ? (
                <button
                  type="button"
                  className={css.open}
                  onClick={() => onOpenScene(Number(note.branchId))}
                >
                  {OPEN_THIS_SCENE}
                </button>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
